import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Star, X } from 'lucide-react';
import { api, ApiError } from '../api';
import { Shell } from '../components/layout/Shell';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { EmptyState } from '../components/ui/empty-state';
import { fmtRelative } from '../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

interface AdminRating {
  id: string;
  job_id: string;
  rater_id: string;
  ratee_id: string;
  role_of_ratee: 'poster' | 'courier';
  stars: number;
  comment: string | null;
  created_at: string;
  job_code: string;
  job_title: string;
  rater_phone: string;
  rater_name: string | null;
  ratee_phone: string;
  ratee_name: string | null;
}

export function RatingsPage() {
  const [params, setParams] = useSearchParams();
  const stars = params.get('stars') ?? '';
  const role = params.get('role') ?? '';
  const q = params.get('q') ?? '';

  const [rows, setRows] = useState<AdminRating[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [draftQ, setDraftQ] = useState(q);

  function patch(next: Record<string, string>) {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v == null || v === '') merged.delete(k);
      else merged.set(k, v);
    }
    setParams(merged);
  }

  function load() {
    setRows(null);
    setErr(null);
    const qs = new URLSearchParams();
    if (stars) qs.set('stars', stars);
    if (role) qs.set('role', role);
    if (q) qs.set('q', q);
    api<{ ratings: AdminRating[] }>(`/api/admin/ratings${qs.toString() ? `?${qs}` : ''}`)
      .then((r) => setRows(r.ratings))
      .catch((e) => setErr(e instanceof ApiError ? `Error ${e.status}` : 'Could not load'));
  }

  useEffect(load, [stars, role, q]);

  const hasFilters = Boolean(stars || role || q);

  return (
    <Shell title="Ratings">
      <PageHeader
        title="Ratings"
        description="All two-sided ratings posted on completed drops."
      />

      <Card className="mb-4">
        <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-center md:gap-2">
          <form
            className="relative flex-1"
            onSubmit={(e) => {
              e.preventDefault();
              patch({ q: draftQ });
            }}
          >
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
            <Input
              value={draftQ}
              onChange={(e) => setDraftQ(e.target.value)}
              placeholder="Search by job code or phone…"
              className="pl-9"
            />
          </form>
          <div className="grid grid-cols-2 gap-2 md:flex md:gap-2">
            <Select value={stars || 'any'} onValueChange={(v) => patch({ stars: v === 'any' ? '' : v })}>
              <SelectTrigger className="md:w-32"><SelectValue placeholder="Stars" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any stars</SelectItem>
                {[5, 4, 3, 2, 1].map((s) => (
                  <SelectItem key={s} value={String(s)}>
                    {s} star{s === 1 ? '' : 's'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={role || 'any'} onValueChange={(v) => patch({ role: v === 'any' ? '' : v })}>
              <SelectTrigger className="md:w-44"><SelectValue placeholder="Rated as" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any rated</SelectItem>
                <SelectItem value="poster">Poster rated</SelectItem>
                <SelectItem value="courier">Courier rated</SelectItem>
              </SelectContent>
            </Select>
            {hasFilters && (
              <Button
                variant="ghost"
                size="sm"
                className="md:h-9"
                onClick={() => {
                  setDraftQ('');
                  setParams(new URLSearchParams());
                }}
              >
                <X className="h-3.5 w-3.5" /> Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {err && (
        <Card className="mb-4 border-error/40 bg-error-tint">
          <CardContent className="py-3 text-sm text-error-on-tint">{err}</CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {!rows && Array.from({ length: 5 }).map((_, i) => (
          <Card key={i}><CardContent className="p-4"><Skeleton className="h-16 w-full" /></CardContent></Card>
        ))}
        {rows && rows.length === 0 && (
          <Card>
            <CardContent>
              <EmptyState
                icon={Star}
                title={hasFilters ? 'No ratings match these filters.' : 'No ratings yet.'}
              />
            </CardContent>
          </Card>
        )}
        {rows?.map((r) => (
          <Card key={r.id}>
            <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-start">
              <div className="flex shrink-0 flex-col items-start md:w-44">
                <span className="tnum text-lg font-semibold tracking-wide text-amber-600 dark:text-amber-400">
                  {'★'.repeat(r.stars)}
                  <span className="text-text-muted">{'★'.repeat(5 - r.stars)}</span>
                </span>
                <Badge className="mt-1">as {r.role_of_ratee}</Badge>
              </div>
              <div className="min-w-0 flex-1">
                {r.comment && (
                  <p className="text-sm text-text-subtle">"{r.comment}"</p>
                )}
                <p className="mt-2 text-xs text-text-muted">
                  <Link to={`/users/${r.rater_id}`} className="hover:text-brand-700 dark:hover:text-brand-500">
                    {r.rater_name ?? r.rater_phone}
                  </Link>
                  {' → '}
                  <Link to={`/users/${r.ratee_id}`} className="hover:text-brand-700 dark:hover:text-brand-500">
                    {r.ratee_name ?? r.ratee_phone}
                  </Link>
                  {' · '}
                  <Link
                    to={`/jobs/${r.job_id}`}
                    className="tnum hover:text-brand-700 dark:hover:text-brand-500"
                  >
                    {r.job_code}
                  </Link>
                  {' · '}
                  {r.job_title}
                </p>
              </div>
              <span className="shrink-0 text-xs text-text-muted md:ml-auto md:self-center">
                {fmtRelative(r.created_at)}
              </span>
            </CardContent>
          </Card>
        ))}
      </div>
    </Shell>
  );
}
